import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CommonLayout from '../../../layouts/Common';
import CommonTable from '../../../components/Common/Table';
import { ActionBtn, HeaderData, NameData, UrlData } from './style';
import { ColumnsType } from 'antd/es/table';
import { DataType } from './type';
import HMAForm from '../../../components/Forms/Admin/HMA';
import { formatDate, getNumericId } from '../../../helpers';
import useListAnalysesView, { useToggleProfessionalViewable } from '../../../hooks/admin/useListAnalysesView';
import { useListAllHMA } from '../../../hooks/admin/useHma';
import useGetAllProfessionals from '../../../hooks/professionals/useGetAllProfessionals';
import { Spin, App } from 'antd';
import { useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { ADMIN_TOGGLE_CLIENT_VIEWABLE } from '../../../services/graphql/mutations/admin';
import { useAdminSignInAsProfessional } from '../../../hooks/admin/useAdminProfessional';

const HMA = () => {
  const token = useSelector((state: any) => state.auth.token);
  const professionals = useSelector((state: any) => state.professionals.professionals || []);
  const [toggleClientMutate] = useMutation(ADMIN_TOGGLE_CLIENT_VIEWABLE);
  const { signInAsProfessional } = useAdminSignInAsProfessional();
  const { message } = App.useApp();
  
  useGetAllProfessionals(); // Fetch all professionals for ID lookup
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { data: hmaData, total, loading, perPage, refetch } = useListAnalysesView('hma', currentPage as number);
  const { toggle, loading: toggleLoading } = useToggleProfessionalViewable();
  const [openNewHMA, setOpenNewHMA] = useState<boolean>(false);
  const navigate = useNavigate();

  const onNavigate = (record: any) => {
    navigate(`/a/hma_results/${record.analysesId || record.id}`, {
      state: {
        payload: record
      }
    });
  };

  const columns: ColumnsType<any> = [
    {
      title: <HeaderData>Client</HeaderData>,
      render: (text, record) => (
        <NameData>{`${record.client || ''}`}</NameData>
      ),
    },
    {
      title: 'GID',
      render: (text, record) => (
        <NameData>{`${record.clientGidId || record.clientGid || ''}`}</NameData>
      ),
    },
    {
      title: <HeaderData>Professional</HeaderData>,
      render: (text, record) => (
        <NameData>{`${record.professional || ''}`}</NameData>
      ),
    },
    {
      title: 'Code',
      render: (text, record) => (
        <NameData>{`${record.professional?.code || record.professionalCode || ''}`}</NameData>
      ),
    },
    {
      title: <HeaderData>Added by</HeaderData>,
      render: (text, record) => (
        <NameData>{`${record.addedBy || 'Admin'}`}</NameData>
      )
    },
    {
      title: <HeaderData>Date</HeaderData>,
      render: (text, record) => (
        <UrlData>{`${formatDate(record.dateOfAnalysis || record.date || record.createdAt)}`}</UrlData>
      ),
    },
    {
      title: <HeaderData>Actions</HeaderData>,
      render: (text, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <ActionBtn
            onClick={() => onNavigate(record)}
            $active={false}
            type="text"
            icon={
              <i className="material-icons-round" style={{ fontSize: 'inherit' }}>visibility</i>
            }
          >
            View
          </ActionBtn>
          <ActionBtn
            onClick={() => onToggle(record)}
            $active={record.professionalViewable}
            type="text"
            icon={
              <i className="material-icons-outlined" style={{ fontSize: 'inherit' }}>policy</i>
            }
          >
            {record.professionalViewable ? 'Disable' : 'Enable'}
          </ActionBtn>
        </div>
      ),
    }
  ];

  const onToggle = async (record: any) => {
    const analysesId = getNumericId(record.analysesId || record.id);
    const profCode = record.professional?.code || record.professionalCode;
    const selectedProf = professionals?.find((p: any) => p.code === profCode);
    const professionalId = getNumericId(record.professional?.id || record.professionalId || selectedProf?.id);
    const clientIdRaw = record.client?.id || record.clientId || record.clientGid || record.clientGidId;
    const clientId = getNumericId(clientIdRaw);

    if (!analysesId || !professionalId || !clientId) {
      message.error('Selection metadata missing.');
      return;
    }

    try {
      // Toggle Professional Viewable
      await toggle({
        analysesId: analysesId,
        analysesType: 'hma',
        professionalId: professionalId
      });

      // Toggle Client Viewable (Syncing for Professional dashboard visibility)
      // Admins are naturally restricted from this mutation, so we assume the Professional's role
      if (selectedProf?.email) {
        try {
          const proSignIn = await signInAsProfessional(selectedProf.email);
          if (proSignIn?.token) {
            await toggleClientMutate({
              variables: {
                input: {
                  analysesId: analysesId,
                  analysesType: 'hma',
                  clientId: record.clientId || clientId.toString()
                }
              },
              context: {
                headers: { Authorization: `Bearer ${proSignIn.token}` }
              }
            });
          }
        } catch (syncErr) {
          console.warn('Sync toggleClientViewable failed: ', syncErr);
          // Optional: notify that only professional view was updated
        }
      }

      await refetch();
      message.success('Visibility updated successfully');
    } catch (err: any) {
      console.error('onToggle Error: ', err);
      message.error(`Failed to update visibility: ${err?.message || 'Error'}`);
    }
  }

  const handleBack = () => {
    setOpenNewHMA(false);
  };

  return (
    <CommonLayout title="HMA" width="1000px">
      {loading ? <Spin size="large" /> :
        <CommonTable
          showNewModal={openNewHMA}
          onNewData={setOpenNewHMA}
          showEditModal={false}
          onEditData={() => {}}
          handleBack={handleBack}
          modalForm={
            <HMAForm
              onReset={openNewHMA}
              onSuccess={async () => {
                console.log('HMA Form onSuccess - Refetching data...');
                try {
                  const result = await refetch();
                  console.log('HMA Form onSuccess - Refetch complete:', result);
                  console.log('HMA Form onSuccess - New data:', result?.data?.listAnalysesView?.data);
                  setOpenNewHMA(false);
                } catch (error) {
                  console.error('HMA Form onSuccess - Refetch error:', error);
                }
              }}
            />
          }
          columns={columns}
          dataSource={hmaData}
          tableType="HMA"
          modalWidth={1300}
          total={`${total} rows`}
          totalData={total}
          page={currentPage}
          setCurrentPage={setCurrentPage}
          perPage={perPage}
        />
      }
    </CommonLayout>
  )
};

export default HMA;